import { Content } from "../../components/Content/Content";
import { FadeInUp } from "../../components/Animation/ScrollAnimation";
import { ColumnsType, FileType } from "../../types/baseTypes";
import { TextMediaOptionsType } from "@/types/ModuleOptionsType";
import Media from "../../components/Media/Media";
import { columnSize } from "../../utils/layout/columnSize";
import { LayoutTypeExport } from "@/types/LayoutType";
import { Modul_Text_Media } from "../../wp/types/acf-module-types";
import { useSublinesVisibility } from "../../hooks/useSublinesOverlinesVisibility";
import { Section } from "../../components/Section/Section";
import { checkModuleIsHidden } from "@/utils/checkModuleIsHidden";
import { BreakpointsOptionsType } from "@/getData/getBreakpoints";
import { DiagramData } from "@/getData/getTextMediaDiagramData";
import { ExtendedLinkType } from "@/components/ExtededLink/ExtendedLink";
import { OptionsFactsheetsType } from "@/types/OptionType";

export type TextMediaModuleType = Modul_Text_Media;

interface TextMediaProps {
  data: TextMediaModuleType;
  textmediaOptions: TextMediaOptionsType;
  breakpoints: BreakpointsOptionsType | null;
  isBigParagraph?: boolean;
  metalCsvFile?: FileType;
  diagramData?: DiagramData;
  factsheets?: OptionsFactsheetsType;
}
interface GetColumns {
  columns?: ColumnsType[] | false;
  media_layout: LayoutTypeExport["media_layout"];
  module_width?: LayoutTypeExport["module_width"];
  disableOffset?: boolean;
  tablet12Columns?: boolean;
}

interface GetColumnsForBreakpoint extends GetColumns {
  breakpoint: ColumnsType["breakpoint"];
}

const TextMedia = ({
  data,
  textmediaOptions,
  isBigParagraph,
  metalCsvFile,
  diagramData,
  factsheets,
}: TextMediaProps) => {
  const {
    background_color_class,
    columns_vertical_align_class,
    headline_text_align_class,
    id,
    module_width,
    row_direction_class,
    section_margin_class,
    section_padding_class,
    text_align_class,
    media_layout,
    row_direction_class_mobile,
  } = data.layout;

  const {
    button_type,
    columns,
    media_format: mediaFormat,
    show_overline_subline,
    show_extended_link,
  } = textmediaOptions ?? {};

  const {
    headline,
    headline_html_tag,
    link,
    content,
    overline,
    subline,
    link_extended,
  } = data.textmedia ?? {};
  const { showOverline, showSubline } = useSublinesVisibility(
    show_overline_subline ?? "none",
  );
  const { mediaColumns, offsetColumns, textColumns } = getColumns({
    columns,
    media_layout,
    module_width,
    tablet12Columns: true,
  });
  // Determine animation direction based on row direction
  // Normal: text left (SlideLeft), media right (SlideRight)
  // Reverse: text right (SlideRight), media left (SlideLeft)

  const textMediaJSX = (
    <>
      <div
        className={`column text-col ${row_direction_class_mobile === "is-column-reverse-mobile" ? "is-12-mobile" : ""} ${textColumns} `}
      >
        {/* jscpd:ignore-start */}
        <FadeInUp>
          <Content
            headline_text_align_class={headline_text_align_class ?? ""}
            text_align_class={text_align_class ?? ""}
            headline_html_tag={headline_html_tag}
            headline={headline}
            text={content ?? ""}
            link={link}
            button_type={button_type}
            overline={showOverline ? overline : ""}
            subline={showSubline ? subline : ""}
            link_extended={
              show_extended_link
                ? (link_extended as ExtendedLinkType)
                : undefined
            }
            factsheets={factsheets}
          />
        </FadeInUp>
        {/* jscpd:ignore-end */}
      </div>

      <div
        className={`column spacer-col is-hidden-touch is-hidden-desktop-only ${offsetColumns}`}
      />
      {data.textmedia.media && (
        <div
          className={`column media-col ${data.textmedia.media.type === "diagram" ? "p-0" : ""} ${row_direction_class_mobile === "is-column-reverse-mobile" ? "is-12-mobile" : ""} ${mediaColumns}`}
        >
          <FadeInUp>
            <Media
              mediaData={data.textmedia.media}
              mediaFormat={mediaFormat}
              showAudioIcon={false}
              sizes="(max-width: 767px) 50vw, (max-width: 1023px) 100vw, (max-width: 1215px) 50vw, (max-width: 1407px) 40vw, 640px"
              metalCsvFile={metalCsvFile}
              diagramData={diagramData}
            />
          </FadeInUp>
        </div>
      )}
    </>
  );

  {
    /* jscpd:ignore-start */
  }
  //! check if module is hidden
  const isHidden = checkModuleIsHidden(data.settings);
  if (isHidden) {
    return null;
  }
  {
    /* jscpd:ignore-end */
  }

  return (
    <Section
      className={`text-media is-clipped ${module_width} ${background_color_class} ${section_padding_class} ${section_margin_class} ${isBigParagraph ? "big-paragraph" : ""}`}
      frameless={module_width === "frameless"}
      // columnsClassName="is-centered is-multiline"
      disableColumns
      id={id}
    >
      {module_width === "normal-frame" || module_width === "frameless" ? (
        <div
          className={`columns is-multiline is-centered ${row_direction_class_mobile === "is-column-reverse-mobile" ? `is-mobile ${row_direction_class_mobile}` : ""} ${columns_vertical_align_class} ${row_direction_class === "is-row-reverse-desktop" ? row_direction_class : ""}`}
        >
          {textMediaJSX}
        </div>
      ) : (
        <div className="columns is-centered">
          <div className={`column ${columnSize(columns)} is-centered`}>
            <div
              className={`columns is-multiline is-centered ${row_direction_class_mobile === "is-column-reverse-mobile" ? `is-mobile ${row_direction_class_mobile}` : ""} ${columns_vertical_align_class} ${row_direction_class === "is-row-reverse-desktop" ? row_direction_class : ""}`}
            >
              {textMediaJSX}
            </div>
          </div>
        </div>
      )}
    </Section>
  );
};

export default TextMedia;

// tablet12Columns: if text and media columns should be 12 columns on tablet (otherwise 6/6, 4/8, 8/4 depdendent on media layout)
export const getColumns = ({
  columns,
  media_layout,
  module_width,
  disableOffset,
  tablet12Columns,
}: GetColumns) => {
  const breakpoints: ColumnsType["breakpoint"][] = [
    "tablet",
    "desktop",
    "widescreen",
    "fullhd",
    "ultrahd",
  ];
  let mediaColumns = "";
  let textColumns = "";
  let offsetColumns = "";
  for (const breakpoint of breakpoints) {
    const { mediaClass, textClass, offsetClass } = getColumnsForBreakpoint({
      breakpoint,
      columns,
      media_layout,
      module_width,
      disableOffset,
      tablet12Columns,
    });
    mediaColumns += mediaClass + " ";
    textColumns += textClass + " ";
    offsetColumns += offsetClass + " ";
  }

  return { mediaColumns, textColumns, offsetColumns };
};

//returns the number of columns for text, media and offset column for each breakpoint
// - narrow-frame: using offsets from options
// - normal-frame: introducig offset from widescrenn on
// tablet12Columns: if text and media columns should be 12 columns on tablet (otherwise 6/6, 4/8, 8/4 depdendent on media layout)
const getColumnsForBreakpoint = ({
  breakpoint,
  columns,
  media_layout,
  module_width = "normal-frame",
  disableOffset = false,
  tablet12Columns = false,
}: GetColumnsForBreakpoint) => {
  let mediaColumns = 0;
  let textColumns = 0;
  let offset = false;
  if (media_layout === "one-half") {
    mediaColumns = 6;
    textColumns = 6;
  }
  if (media_layout === "one-third") {
    mediaColumns = 4;
    textColumns = 8;
  }
  if (media_layout === "two-thirds") {
    mediaColumns = 8;
    textColumns = 4;
  }

  if (breakpoint === "tablet" && tablet12Columns) {
    mediaColumns = 12;
    textColumns = 12;
    disableOffset = true;
  }

  if (!disableOffset) {
    if (module_width === "narrow-frame") {
      if (!columns) {
        throw new Error("columns required for module_width: narrow-frame");
      }
      //if text media is in grid respect the columns from options and their offsets
      if (
        columns.find((column) => column.breakpoint === breakpoint)
          ?.column_offset
      ) {
        textColumns--;
        offset = true;
      }
    }

    // CHANGE THIS IF OFFSET BREAKPOINTS CHANGE FOR FULL WIDTH
    if (module_width === "normal-frame") {
      //if text media is full width, introduce gap from widescreen on
      if (
        breakpoint === "widescreen" ||
        breakpoint === "fullhd" ||
        breakpoint === "ultrahd"
      ) {
        mediaColumns--;
        offset = true;
      }
    }
  }

  const offsetClass = offset
    ? `is-1-${breakpoint}`
    : `is-hidden-${breakpoint}-only`;
  const mediaClass = `is-${mediaColumns}-${breakpoint}`;
  const textClass = `is-${textColumns}-${breakpoint}`;

  return { mediaClass, textClass, offsetClass };
};
